// ═══════════════════════════════════════════════════════
// GLOBAL CSS — injected once at app startup
// Brand: Inter + Playfair Display + JetBrains Mono
// ═══════════════════════════════════════════════════════

const FONT_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Playfair+Display:wght@400;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');

:root {
  /* Design tokens as CSS custom properties */
  --lpa-font-sans: 'Inter', 'Work Sans', system-ui, -apple-system, sans-serif;
  --lpa-font-serif: 'Playfair Display', Georgia, serif;
  --lpa-font-mono: 'JetBrains Mono', 'SF Mono', Consolas, monospace;
  --lpa-accent: #B87333;
  --lpa-accent-hover: #B56A3A;
  --lpa-radius-sm: 6px;
  --lpa-radius-md: 10px;
  --lpa-radius-lg: 14px;
  --lpa-transition-fast: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  --lpa-transition-normal: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

*, *::before, *::after { box-sizing: border-box; }

html {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
}

body {
  font-family: var(--lpa-font-sans);
  line-height: 1.5;
  margin: 0;
}

/* Monospace for financial data */
.lpa-mono, [data-mono] {
  font-family: var(--lpa-font-mono);
  font-variant-numeric: tabular-nums;
}

/* Serif for display headings */
.lpa-serif {
  font-family: var(--lpa-font-serif);
}
`;

const SKELETON_CSS = `
@keyframes lpa-shimmer {
  0%   { background-position: -400px 0; }
  100% { background-position:  400px 0; }
}
@keyframes lpa-fade-in {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes lpa-spin {
  to { transform: rotate(360deg); }
}
.lpa-skeleton {
  background: linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%);
  background-size: 400px 100%;
  animation: lpa-shimmer 1.4s infinite;
  border-radius: var(--lpa-radius-sm);
}
.lpa-skeleton-dark {
  background: linear-gradient(90deg,rgba(255,255,255,0.06) 25%,rgba(255,255,255,0.12) 50%,rgba(255,255,255,0.06) 75%);
  background-size: 400px 100%;
  animation: lpa-shimmer 1.4s infinite;
  border-radius: var(--lpa-radius-sm);
}
.lpa-fade-in { animation: lpa-fade-in 0.3s cubic-bezier(0.4,0,0.2,1) both; }
.lpa-spinner { animation: lpa-spin 1s linear infinite; }
@keyframes lpa-modal-in {
  from { opacity: 0; transform: translateY(16px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes lpa-overlay-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}
.lpa-modal-enter { animation: lpa-modal-in 0.25s cubic-bezier(0.4,0,0.2,1) both; }
.lpa-overlay-enter { animation: lpa-overlay-in 0.2s ease both; }
@media (prefers-reduced-motion: reduce) {
  .lpa-modal-enter, .lpa-overlay-enter { animation: none; }
}
`;

const MOBILE_CSS = `
/* ── Table wrappers: horizontal scroll ── */
.mobile-table-wrap,
.lpa-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.lpa-table-wrap { border-radius: 0 0 var(--lpa-radius-lg) var(--lpa-radius-lg); }

/* ── Charts never overflow container ── */
.recharts-wrapper, .recharts-surface { max-width: 100% !important; }

/* ── Tablet breakpoint (≤1024px) ── */
@media (max-width: 1024px) {
  .lpa-metric-grid {
    grid-template-columns: repeat(2, 1fr) !important;
  }
  .hide-tablet { display: none !important; }
  .lpa-modal-card { max-width: 90vw !important; margin: 16px !important; }
}

/* ── Mobile breakpoint (≤640px) ── */
@media (max-width: 640px) {
  .hide-mobile { display: none !important; }
  .col-mobile-full { grid-column: 1 / -1 !important; }

  /* Auto-scroll tables */
  div:has(> table) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  table { min-width: unset; }
  .lpa-table-wrap table,
  .mobile-table-wrap table,
  div:has(> table) table { min-width: 480px; }

  /* Sidebar & period bar */
  .lpa-sidebar-toggle { display: none !important; }
  .lpa-period-bar { flex-wrap: wrap; gap: 6px !important; }

  /* Metric cards: 2 columns */
  .lpa-metric-grid {
    grid-template-columns: 1fr 1fr !important;
  }

  /* Section titles smaller */
  .lpa-section-title { font-size: 14px !important; }

  /* Touch-friendly buttons */
  button { min-height: 40px; }

  /* Modals full width */
  .lpa-modal-card { max-width: 100% !important; border-radius: var(--lpa-radius-lg) !important; }
}

/* ── Small phone (≤480px) ── */
@media (max-width: 480px) {
  .lpa-metric-grid {
    grid-template-columns: 1fr !important;
  }
  table { font-size: 11px !important; }
}
`;

const ACCESSIBILITY_CSS = `
/* ══════════════════════════════════════════════════════════
   ACCESSIBILITY & INTERACTIONS
   Focus rings, transitions, and interaction states
   Brand: copper accent focus, smooth easing
   ═════════════════════════════════════════════════════════ */

/* ── Smooth transitions for interactive elements ── */
button, a, input, select, textarea {
  transition: var(--lpa-transition-fast);
}

/* ── Focus visible ring (brand copper) ── */
button:focus-visible,
a:focus-visible,
input:focus-visible,
select:focus-visible,
textarea:focus-visible,
:focus-visible {
  outline: 2px solid var(--lpa-accent);
  outline-offset: 2px;
}

/* ── Button states ── */
button:not(:disabled) { cursor: pointer; }
button:not(:disabled):hover { transform: translateY(-1px); }
button:not(:disabled):active { transform: translateY(0); }

/* ── Input focus states ── */
input:focus, select:focus, textarea:focus {
  border-color: var(--lpa-accent);
}

/* ── Disabled states ── */
button:disabled, input:disabled, select:disabled, textarea:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── Link hover ── */
a:not(.lpa-no-hover) {
  transition: opacity 0.2s ease, color 0.2s ease;
}
a:not(.lpa-no-hover):hover { opacity: 0.85; }

/* ── Selection color (brand midnight + platinum) ── */
::selection {
  background: #B87333;
  color: #0F172A;
}

/* ── Reduce motion ── */
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}

/* ── Remove outline on :focus when not using keyboard ── */
:focus:not(:focus-visible) { outline: none; }

/* ── Scrollbar styling (dark theme) ── */
::-webkit-scrollbar { width: 8px; height: 8px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb {
  background: rgba(184,115,51,0.25);
  border-radius: var(--lpa-radius-full, 9999px);
}
::-webkit-scrollbar-thumb:hover { background: rgba(184,115,51,0.4); }

/* ── Theme transition (dark/light toggle) ── */
body.theme-transitioning,
body.theme-transitioning * {
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease !important;
}

/* ── Print styles ── */
@media print {
  .no-print { display: none !important; }
  body { background: white !important; color: black !important; }
}
`;

/** Inject a single <style> block idempotently. */
function injectStyle(id: string, css: string): void {
  if (document.getElementById(id)) return;
  const s = document.createElement("style");
  s.id = id;
  s.textContent = css;
  document.head.appendChild(s);
}

/** Inject global CSS once (idempotent). Call at app startup. */
export function injectGlobalStyles(): void {
  if (typeof document === "undefined") return;
  injectStyle("lpa-font-css", FONT_CSS);
  injectStyle("lpa-skeleton-css", SKELETON_CSS);
  injectStyle("lpa-mobile-css", MOBILE_CSS);
  injectStyle("lpa-accessibility-css", ACCESSIBILITY_CSS);
}

// ── D3: Theme CSS Variables ─────────────────────────────────────

import type { ThemeObject } from "./theme.js";

/**
 * Inject theme tokens as CSS custom properties on :root.
 * Called on every theme toggle so all pages auto-adapt.
 */
export function applyThemeCSSVars(T: ThemeObject, dark: boolean): void {
  if (typeof document === "undefined") return;

  const root = document.documentElement;
  root.style.setProperty("--lpa-bg", T.bg);
  root.style.setProperty("--lpa-card", T.card);
  root.style.setProperty("--lpa-text", T.text);
  root.style.setProperty("--lpa-sub", T.sub);
  root.style.setProperty("--lpa-border", T.border);
  root.style.setProperty("--lpa-muted", T.muted);
  root.style.setProperty("--lpa-row1", T.row1);
  root.style.setProperty("--lpa-row2", T.row2);
  root.style.setProperty("--lpa-accent", T.accent);
  root.style.setProperty("--lpa-accent-hover", T.accentHover);
  root.style.setProperty("--lpa-accent-text", T.accentText);
  root.style.setProperty("--lpa-surface", T.surface);
  root.style.setProperty("--lpa-overlay", T.overlay);
  root.style.setProperty("--lpa-success", T.success);
  root.style.setProperty("--lpa-danger", T.danger);
  root.style.setProperty("--lpa-warning", T.warning);
  root.style.setProperty("--lpa-info", T.info);
  root.style.setProperty("--lpa-success-bg", T.successBg);
  root.style.setProperty("--lpa-danger-bg", T.dangerBg);
  root.style.setProperty("--lpa-warning-bg", T.warningBg);
  root.style.setProperty("--lpa-info-bg", T.infoBg);
  root.style.setProperty("--lpa-focus-ring", T.focusRing);

  // Body class for third-party / global CSS selectors
  document.body.classList.toggle("lpa-dark", dark);
  document.body.classList.toggle("lpa-light", !dark);
  document.body.style.backgroundColor = T.bg;
  document.body.style.color = T.text;

  // Smooth transition on toggle
  document.body.classList.add("theme-transitioning");
  setTimeout(() => document.body.classList.remove("theme-transitioning"), 350);
}

/**
 * Detect system color-scheme preference for first-time visitors.
 * Returns true for dark preference, false for light.
 */
export function detectSystemTheme(): boolean {
  if (typeof window === "undefined") return true; // default dark
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}
